import api, { setMemberToken, clearMemberToken } from "./api";

// Backend routes (see SFMIS-Backend/routes/web.php + routes/api.php):
//   POST /send-otp                 { email }        → { account_exists, registration_in_progress, ... }
//   POST /verify-otp               { email, otp }   → { account_exists, token, member, redirect_to, ... }
//   POST /api/member-registrations (multipart form data, session-gated by OTP)
//   GET  /api/member/me            (member bearer token)
//   POST /api/member/logout        (member bearer token)

// Pre-OTP existence check (POST /api/member/check). Public endpoint that reports
// whether an email already has an account or a registration still in progress,
// WITHOUT sending an OTP. Response:
//   { account_exists, registration_in_progress, flow, member, registration_process, ... }
// The login & registration flows call this first to decide whether to send a code.
export const checkMember = (email) =>
  api.post("/api/member/check", { email }).then((res) => res.data);

export const sendOtp = (email) =>
  api.post("api/send-otp", { email }).then((res) => res.data);

// verify-otp now issues a Sanctum bearer token for existing members. Capture it
// here (centralized) so every caller — login and the registration pages —
// automatically establishes the member session on a successful verify.
export const verifyOtp = (email, otp) =>
  api.post("api/verify-otp", { email, otp }).then((res) => {
    const data = res.data || {};
    if (data.token) setMemberToken(data.token);
    return data;
  });

// The logged-in member's own record (works with the member bearer token).
export const getMemberMe = () =>
  api.get("/api/member/me").then((res) => res.data?.member ?? res.data);

// Ends the member session: revokes the token server-side, then clears it
// locally regardless of the request outcome.
export const memberLogout = () =>
  api
    .post("/api/member/logout")
    .catch(() => {})
    .finally(() => clearMemberToken());

// Note: do NOT set Content-Type manually here. When the body is a FormData,
// axios/the browser sets "multipart/form-data" together with the required
// boundary automatically; setting it by hand can drop the boundary.
export const registerMember = (formData) =>
  api.post("/api/member-registrations", formData).then((res) => res.data);

// Server-side validation used BEFORE the summary step. Same multipart payload as
// registerMember, but the backend only validates (no insert): resolves with
// { valid, data } on success, or rejects with 422 { message, errors } that the
// form maps back onto its fields.
export const previewMemberRegistration = (formData) =>
  api.post("/api/member-registrations/preview", formData).then((res) => res.data);

// Confirms whether a registration process's payment has actually cleared with
// the real gateway . Called by PaymentMethod once the CyberExt popup closes —
// the popup itself is cross-origin, so this is the only way to learn the
// outcome. Response: { payment_status, payment_completed, next_step, ... }.
export const checkRegistrationPaymentStatus = ({ process_type, process_id, payment_reference }) => {
  const requestSentAt = Date.now();
  console.log(`[payment-status] request sent at ${new Date(requestSentAt).toISOString()}`);
  return api
    .post("/api/registration-payments/status", { process_type, process_id, payment_reference })
    .then((res) => {
      const responseReceivedAt = Date.now();
      console.log(
        `[payment-status] response received at ${new Date(responseReceivedAt).toISOString()} (delay: ${responseReceivedAt - requestSentAt}ms)`
      );
      return res.data;
    });
};
